/**
 * ============================================================================
 * CURRENT USER — the alias the SERVER attributes our writes to.
 * ============================================================================
 *
 * The UI needs this for one purpose: gate the author-only Edit/Delete
 * affordances on comments and replies with the same alias the server enforces
 * author-only updates with.
 *
 * Three sources, in priority order:
 *
 *  1. A Mons-injected login meta tag, when the page is served through a
 *     Midway-authenticated host. Our deployed stages are NOT: the website is
 *     plain CloudFront/S3 (HILTBusinessReportingUICDK) and serves the page
 *     anonymously, so no tag is present there. It is kept first because it is
 *     authoritative wherever it does exist (Mons-hosted dev, future embed in
 *     the Midway-protected project portal).
 *
 *  2. THE MIDWAY id_token. The bootstrap SSO check (accessors/midwayAuth.ts,
 *     spec midway-client-auth) mints an id_token from the live Midway session
 *     and registers its `sub` claim here via setMidwayAlias — so on deployed
 *     stages the real login is known at first paint, before the user has
 *     authored anything.
 *
 *  3. LEARNED FROM THE SERVER. Writes carry an `actor`, but the backend
 *     overrides it with its own authenticated Midway identity
 *     (PublishMetricCommentActivity), so the STORED author is the real alias
 *     and the client's guess is not. We therefore remember the ids of records
 *     this browser authored and, when a later list response returns one, read
 *     its actor back — that string IS our alias. It is cached in localStorage,
 *     so it survives reloads and the gate keeps working on records written in
 *     earlier sessions.
 *
 * Without (2) and (3) the fallback below stood in for a real identity on
 * every deployed stage, so `author === currentUserAlias()` was false for
 * everyone and NOBODY could edit or delete their own comment.
 *
 * This is a UX affordance only, never a trust boundary — the server is the one
 * that rejects a non-author's update.
 */

/** Local-dev stand-in when no alias is known (kept from the pre-Midway placeholder). */
export const FALLBACK_ACTOR = 'wbr-user';

const MONS_LOGIN_META_TAG = 'a2z:mons_login';
const MIDWAY_LOGIN_META_TAG = 'a2z:login';

/** Where the learned alias is cached across reloads. */
const LEARNED_ALIAS_KEY = 'wbr.callouts.actor';

/** Authors we must never treat as an identity (the mapper's placeholder). */
const NOT_AN_ACTOR = new Set(['', 'unknown']);

/** Ids of records this browser authored, so their own Edit/Delete show at once. */
const ownRecordIds = new Set<string>();

/** Cached learned alias; `null` = looked in storage and found nothing. */
let learnedActor: string | null | undefined;

/** Alias from the Midway id_token `sub` (accessors/midwayAuth.ts). */
let midwayActor: string | undefined;

const readMetaTagContent = (name: string): string | undefined =>
  document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content ||
  undefined;

const metaActor = (): string | undefined =>
  readMetaTagContent(MONS_LOGIN_META_TAG) ??
  readMetaTagContent(MIDWAY_LOGIN_META_TAG);

/** localStorage throws in private-mode Safari and when storage is disabled. */
function readStoredActor(): string | null {
  try {
    return window.localStorage.getItem(LEARNED_ALIAS_KEY) || null;
  } catch {
    return null;
  }
}

function storeActor(actor: string): void {
  try {
    window.localStorage.setItem(LEARNED_ALIAS_KEY, actor);
  } catch {
    // In-memory only for this page: the gate still works, it just doesn't
    // survive a reload.
  }
}

/**
 * Register the alias decoded from the Midway id_token. Blank input is
 * ignored — the backend rejects a blank actor, so it must never become one.
 */
export function setMidwayAlias(alias: string): void {
  const trimmed = alias.trim();
  if (trimmed && !NOT_AN_ACTOR.has(trimmed.toLowerCase())) {
    midwayActor = trimmed;
  }
}

/**
 * The caller's alias: the injected login when the page is authenticated, else
 * the Midway id_token identity, else the alias learned from the server, else
 * the local-dev fallback.
 */
export function currentUserAlias(): string {
  if (learnedActor === undefined) learnedActor = readStoredActor();
  return metaActor() ?? midwayActor ?? learnedActor ?? FALLBACK_ACTOR;
}

/**
 * Note a record id this browser just created. Two effects: its Edit/Delete
 * appear immediately (before any alias is known), and the next list response
 * teaches us the alias the server stored for it.
 */
export function rememberOwnRecord(id: string | undefined): void {
  if (id) ownRecordIds.add(id);
}

/** True when this browser authored the record in this session. */
export function isOwnRecord(id: string): boolean {
  return ownRecordIds.has(id);
}

/**
 * Learn our alias from records we know we authored. Pairs come from the list
 * response (comments AND replies); the first one whose id we remember carries
 * the server's idea of who we are.
 */
export function learnActorFrom(
  records: Array<{ id: string; actor: string }>,
): void {
  // A higher-priority identity (injected login or Midway token) already wins.
  if (metaActor() || midwayActor) return;
  for (const rec of records) {
    if (!ownRecordIds.has(rec.id)) continue;
    const actor = rec.actor.trim();
    if (NOT_AN_ACTOR.has(actor.toLowerCase())) continue;
    if (actor === learnedActor) return;
    learnedActor = actor;
    storeActor(actor);
    return;
  }
}

/**
 * Do two author strings name the same person? Compared case-insensitively and
 * with any email domain dropped, because the alias reaches us from three
 * places (meta tag, server record, local fallback) that need not agree on
 * `jdoe` vs `JDoe` vs `jdoe@amazon.com`. An unknown author matches nobody.
 */
export function sameActor(left: string, right: string): boolean {
  const normalize = (value: string) => value.trim().toLowerCase().split('@')[0];
  const a = normalize(left);
  const b = normalize(right);
  if (NOT_AN_ACTOR.has(a) || NOT_AN_ACTOR.has(b)) return false;
  return a === b;
}

/** TEST ONLY — drop the aliases and the authored-record ids. */
export function resetActorLearning(): void {
  ownRecordIds.clear();
  learnedActor = undefined;
  midwayActor = undefined;
  try {
    window.localStorage.removeItem(LEARNED_ALIAS_KEY);
  } catch {
    // Nothing cached, nothing to clear.
  }
}
